import React from 'react';
import { motion } from 'framer-motion';
import { Linkedin, Instagram, Mail, Phone, MapPin } from 'lucide-react';
import { useContent } from '../context/ContentContext';

const socialIcons = {
  LinkedIn: Linkedin,
  Instagram: Instagram,
};

const Footer = () => {
  const { footerContent, siteConfig } = useContent();

  // Custom quick links (removed About, added The Success Blueprint)
  const customQuickLinks = [
    { name: "The Success Blueprint", href: "#framework" },
    { name: "Programs", href: "#programs" },
  ];

  return (
    <footer className="bg-[var(--bg-card)] border-t border-[var(--border-primary)]" data-testid="footer-section">
      <div className="container-main py-16">
        <div className="grid md:grid-cols-3 gap-12">
          {/* Brand Column */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="md:col-span-1"
          >
            <img
              src={siteConfig.logo}
              alt={siteConfig.name}
              className="h-24 w-auto mb-6 object-contain"
            />
            {/* Social Icons */}
            <div className="flex gap-3">
              {footerContent.social.map((item) => {
                const Icon = socialIcons[item.name];
                return (
                  <a
                    key={item.name}
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-full border border-[var(--border-primary)] flex items-center justify-center text-[var(--text-muted)] hover:text-[var(--accent-teal)] hover:border-[var(--accent-teal)] transition-colors"
                    aria-label={item.name}
                  >
                    <Icon className="w-4 h-4" />
                  </a>
                );
              })}
            </div>
          </motion.div>

          {/* Quick Links */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <h4 className="font-display font-semibold text-[var(--text-primary)] mb-4">
              Quick Links
            </h4>
            <ul className="space-y-3">
              {customQuickLinks.map((link) => (
                <li key={link.name}>
                  <a
                    href={link.href}
                    className="text-[var(--text-muted)] hover:text-[var(--accent-teal)] transition-colors text-sm"
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Contact */}
          <motion.div
            id="footer-contact"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <h4 className="font-display font-semibold text-[var(--text-primary)] mb-4">
              Contact
            </h4>
            <ul className="space-y-3">
              <li className="flex items-center gap-3 text-[var(--text-muted)] text-sm">
                <Mail className="w-4 h-4 text-[var(--accent-teal)] flex-shrink-0" />
                <a href={`mailto:${footerContent.contact.email}`} className="hover:text-[var(--accent-teal)] transition-colors">
                  {footerContent.contact.email}
                </a>
              </li>
              <li className="flex items-center gap-3 text-[var(--text-muted)] text-sm">
                <Phone className="w-4 h-4 text-[var(--accent-teal)] flex-shrink-0" />
                <a href={`tel:${footerContent.contact.phone}`} className="hover:text-[var(--accent-teal)] transition-colors">
                  {footerContent.contact.phone}
                </a>
              </li>
              <li className="flex items-start gap-3 text-[var(--text-muted)] text-sm">
                <MapPin className="w-4 h-4 text-[var(--accent-teal)] mt-0.5 flex-shrink-0" />
                {footerContent.contact.location}
              </li>
            </ul>
          </motion.div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-[var(--border-primary)] mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-sm text-[var(--text-muted)]">
            © {new Date().getFullYear()} CommfluentBiz. All rights reserved.
          </p>
          <div className="flex gap-6">
            <a href="#" className="text-sm text-[var(--text-muted)] hover:text-[var(--accent-teal)] transition-colors">
              Privacy Policy
            </a>
            <a href="#" className="text-sm text-[var(--text-muted)] hover:text-[var(--accent-teal)] transition-colors">
              Terms of Service
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
